import { Flame, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { streakTileClass, streakTileIdleClass } from "@/lib/card-styles";
import { typeNumericClass, typeCaptionClass } from "@/lib/typography";
import { useLanguage } from "@/i18n";

import type { ProfileMenuGamificationProps } from "./types";

export interface ProfileStreakBandProps {
  gamification: ProfileMenuGamificationProps;
  onOpen?: () => void;
  className?: string;
}

/** Fixed segment count, so the gauge reads as one object at any percentage. */
const SEGMENTS = 16;

/**
 * The ONE context strip in the profile menu: streak, level, XP.
 *
 * ## Why a band and not three stat cards
 *
 * Calm focus allows at most one stat row before the content, and this menu's
 * content is the menu rows underneath. So the three numbers share a single
 * tile with one gauge under them, instead of the three bordered pills this
 * used to be.
 *
 * ## Why the whole band is the target
 *
 * Streak is the one number in here a learner acts on ("don't break it"), so
 * the band opens the streak detail rather than the profile. It used to be a
 * bare `<button>` floating between two separators with no framing, which read
 * as a stray fragment of a card rather than part of the menu.
 *
 * ## Zero is a state, not a number
 *
 * A streak of 0 used to render "0 day streak" next to a grey flame. The
 * vocabulary streak indicator already answers this better — "Start today" —
 * because zero is something to do, not something to have.
 *
 * ## Why the gauge is chunked, and amber
 *
 * A hairline-width continuous bar at 288px is unreadable below about 20% and
 * looks identical to a track that has not loaded yet. Sixteen discrete
 * segments (the `AiWorkProgress` rule) make any non-zero progress visibly
 * *non-zero*, and tinting the whole band amber while a streak is live is the
 * signal the rest of the product already gives "you are on a run".
 */
export function ProfileStreakBand({ gamification, onOpen, className }: ProfileStreakBandProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { level, xp, currentStreak, levelProgress } = gamification;

  const isActive = currentStreak > 0;
  const progress = Math.min(100, Math.max(0, levelProgress));
  const filledSegments = Math.round((progress / 100) * SEGMENTS);

  // Numbers must go through the app's language, not the browser's
  // (`useLanguage().format`) — a bare `toLocaleString()` here formats against
  // whatever locale the OS is set to, which is not the one the reader picked.
  const xpLabel = language?.format ? language.format.number(xp) : xp.toLocaleString();

  // `DropdownMenuItem`, not a bare `<button>`: the band opens a dialog, and a
  // plain button inside a Radix menu is not something the menu knows how to
  // dismiss — it stayed mounted and `data-state="open"` behind the dialog,
  // still holding the menu's focus trap. `onSelect` also puts the band in the
  // arrow-key rotation, which a raw button was never part of.
  return (
    <DropdownMenuItem
      onSelect={onOpen}
      aria-label={
        isActive
          ? t("nav.avatar.streak.openActive", {
              defaultValue: "{{count}} day streak — open streak details",
              count: currentStreak,
            })
          : t("nav.avatar.streak.openIdle", {
              defaultValue: "No active streak — open streak details",
            })
      }
      className={cn(
        "block w-full cursor-pointer rounded-xl p-2.5 text-left outline-none",
        "transition-shadow focus-visible:ring-2 focus-visible:ring-ring",
        isActive
          ? "border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/70 dark:border-amber-900/60 dark:from-amber-950/40 dark:to-orange-950/20"
          : "border border-stone-200 bg-stone-50 dark:border-stone-800 dark:bg-stone-800/40",
        className,
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            isActive ? streakTileClass : streakTileIdleClass,
          )}
          aria-hidden="true"
        >
          <Flame className="h-[18px] w-[18px]" />
        </span>

        <span className="flex min-w-0 flex-1 items-baseline gap-2">
          {isActive ? (
            <>
              <span className={cn("shrink-0 text-2xl font-bold leading-none", typeNumericClass)}>
                {currentStreak}
              </span>
              <span className="truncate text-sm font-semibold text-amber-800 dark:text-amber-300">
                {t("nav.avatar.streak.dayWord", "day streak")}
              </span>
            </>
          ) : (
            <span className="truncate text-sm font-semibold text-stone-600 dark:text-stone-300">
              {t("nav.avatar.streak.idle", "Start a streak today")}
            </span>
          )}
        </span>

        <span
          className={cn(
            "flex shrink-0 items-center gap-1 rounded-full bg-background/70 px-2 py-1 backdrop-blur-sm",
            "text-[11px] font-semibold text-primary",
          )}
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          <span className={typeNumericClass}>
            {t("nav.avatar.level", { defaultValue: "Lv. {{level}}", level })}
          </span>
        </span>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          aria-label={t("nav.avatar.levelProgress", { defaultValue: "Progress to the next level" })}
          className="flex min-w-0 flex-1 gap-[3px]"
        >
          {Array.from({ length: SEGMENTS }, (_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors duration-500",
                i < filledSegments
                  ? isActive
                    ? "bg-gradient-to-r from-amber-400 to-orange-500"
                    : "bg-primary"
                  : "bg-stone-200 dark:bg-stone-700",
              )}
            />
          ))}
        </div>
        <span className={cn("shrink-0 tabular-nums", typeCaptionClass)}>
          {t("nav.avatar.xp", { defaultValue: "{{count}} XP", count: xpLabel })}
        </span>
      </div>
    </DropdownMenuItem>
  );
}

export default ProfileStreakBand;